import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  TrophyIcon,
  MapPinIcon,
  ClockIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface UserDashboardHomeProps {
  tournamentId: string;
}

interface TournamentInfo {
  name: string;
  start_date: string;
  end_date: string;
  venue: string;
  status: string;
}

interface UpcomingEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
}

interface UserProfile {
  email: string;
  full_name: string;
  role: string;
}

export default function UserDashboardHome({ tournamentId }: UserDashboardHomeProps) {
  const [tournament, setTournament] = useState<TournamentInfo | null>(null);
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const { data: authUser } = await supabase
          .from('auth_users')
          .select('email, full_name, role')
          .eq('id', user.id)
          .maybeSingle();

        setProfile(authUser ? {
          email: authUser.email,
          full_name: authUser.full_name || user.user_metadata?.full_name || 'User',
          role: authUser.role || 'user',
        } : {
          email: user.email || '',
          full_name: user.user_metadata?.full_name || 'User',
          role: 'user',
        });
      }

      if (tournamentId) {
        const { data: tournamentData } = await supabase
          .from('tournaments')
          .select('name, start_date, end_date, venue, status')
          .eq('id', tournamentId)
          .maybeSingle();

        if (tournamentData) {
          setTournament(tournamentData);
        }

        const { data: eventsData } = await supabase
          .from('events')
          .select('id, title, date, time, location')
          .eq('tournament_id', tournamentId)
          .gte('date', new Date().toISOString().split('T')[0])
          .order('date', { ascending: true })
          .limit(5);

        if (eventsData) {
          setEvents(eventsData);
        }
      }
    } catch (err) {
      console.error('Error fetching user dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-gradient-to-br from-golf-green to-emerald-700 rounded-2xl p-8 text-white shadow-lg"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-1">
              Welcome back, {profile?.full_name || 'User'}
            </h1>
            <p className="text-white/80 text-sm">
              {profile?.email}
            </p>
          </div>
          <div className="hidden sm:block">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
              <UserCircleIcon className="w-10 h-10 text-white/90" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Tournament Info */}
      {tournament && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-amber-50 p-2.5 rounded-lg">
              <TrophyIcon className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Current Tournament</h2>
          </div>
          <div className="pl-12">
            <h3 className="text-xl font-bold text-gray-900 mb-3">{tournament.name}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-2 text-gray-600">
                <CalendarIcon className="w-4 h-4 text-gray-400" />
                <span className="text-sm">
                  {formatDate(tournament.start_date)} - {formatDate(tournament.end_date)}
                </span>
              </div>
              {tournament.venue && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPinIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{tournament.venue}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  tournament.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                  tournament.status === 'active' ? 'bg-green-100 text-green-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {tournament.status.charAt(0).toUpperCase() + tournament.status.slice(1)}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Upcoming Events */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="bg-blue-50 p-2.5 rounded-lg">
            <CalendarIcon className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Upcoming Events</h2>
        </div>

        {events.length > 0 ? (
          <div className="space-y-3 pl-12">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.05 * index }}
                className="flex items-center gap-4 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-white rounded-lg border border-gray-200 flex flex-col items-center justify-center">
                  <span className="text-xs text-gray-500 leading-tight">
                    {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                  </span>
                  <span className="text-sm font-bold text-gray-900 leading-tight">
                    {new Date(event.date).getDate()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{event.title}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    {event.time && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <ClockIcon className="w-3 h-3" />
                        {event.time}
                      </span>
                    )}
                    {event.location && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPinIcon className="w-3 h-3" />
                        {event.location}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="pl-12 py-8 text-center">
            <CalendarIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No upcoming events scheduled</p>
          </div>
        )}
      </motion.div>

      {/* Account Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="bg-gray-100 p-2.5 rounded-lg">
            <UserCircleIcon className="w-5 h-5 text-gray-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Your Account</h2>
        </div>

        <div className="pl-12 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</label>
              <p className="text-sm text-gray-900 mt-0.5">{profile?.full_name}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email</label>
              <p className="text-sm text-gray-900 mt-0.5">{profile?.email}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Role</label>
              <p className="text-sm text-gray-900 mt-0.5 capitalize">{profile?.role}</p>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100">
            <button
              onClick={handleSignOut}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
